import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Movie as MovieIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Subtitles as SubtitlesIcon,
} from '@mui/icons-material';
import { useAlbum } from '../../contexts/AlbumContext';
import { useApi } from '../../hooks/useApi';

const GenerateStep = () => {
  const { loading, videoStatus } = useAlbum();
  const { generateVideo, downloadVideo } = useApi();

  const isProcessing = videoStatus === 'processing';
  const isCompleted = videoStatus === 'completed';

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {isCompleted ? (
            <CheckCircleIcon
              sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
            />
          ) : (
            <MovieIcon
              sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
            />
          )}
          <Typography variant="h2" gutterBottom>
            {isCompleted ? '동영상 완성!' : '동영상 생성'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isCompleted
              ? '동영상이 준비되었습니다. 다운로드하세요!'
              : '모든 준비가 완료되었습니다. 최종 동영상을 생성합니다.'}
          </Typography>
        </CardContent>
      </Card>

      {isProcessing && (
        <Card sx={{ bgcolor: 'info.light' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  동영상 생성 중...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  완료까지 시간이 걸릴 수 있습니다.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {!isCompleted && !isProcessing && (
        <>
          <Card sx={{ bgcolor: 'secondary.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SubtitlesIcon sx={{ mr: 1, color: 'secondary.dark' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  장소 정보 자막
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                편집 단계에서 입력한 장소명과 활동 정보가 동영상 자막으로 표시됩니다.
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HourglassEmptyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle2">
                  생성 예상 시간
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                사진 수와 서버 상태에 따라 1~5분 정도 소요됩니다.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      {isCompleted ? (
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={downloadVideo}
          fullWidth
          startIcon={<DownloadIcon />}
          sx={{ py: 1.5 }}
        >
          동영상 다운로드
        </Button>
      ) : (
        <Button
          variant="contained"
          size="large"
          onClick={generateVideo}
          disabled={loading || isProcessing}
          fullWidth
          startIcon={<MovieIcon />}
          sx={{ py: 1.5 }}
        >
          {isProcessing ? '생성 중...' : '동영상 생성'}
        </Button>
      )}

      {isCompleted && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center', display: 'block' }}
        >
          * 다운로드한 동영상은 MP4 형식입니다.
        </Typography>
      )}
    </Stack>
  );
};

export default GenerateStep;
